import { FormattedMessage } from 'react-intl';
import { toastr } from 'react-redux-toastr';

import AffordableWaitlistActionModal from '../AffordableWaitlistActionModal';
import { useDenyFromWaitlist } from '../hooks';
import { denyReasons, WAITLIST_ACTION_SCOPE } from '../constants';
import messages from '../messages';
import useReactHookFormProps from '../../../hooks/useReactHookFormProps';
import useHasPermission from '../../../hooks/useHasPermission';
import PermissionGatedActions from '../PermissionGatedActions';

const DenyFromWaitlist = ({
  organizationId,
  propertyId,
  affordableWaitlistId,
  open,
  handleClose,
  onSuccess,
}) => {
  const { mutate: denyMutate, isLoading: isSubmitting } = useDenyFromWaitlist({
    organizationId,
    propertyId,
    options: {
      onSuccess: () => {
        toastr.success('Success', 'Applicant denied successfully');
        // Deliberately no handleClose() here: handleClose means "the user
        // dismissed me", and firing it on success would clear the caller's
        // pending-action bookkeeping before onSuccess could read it — and a
        // late success from an already-dismissed modal would close whatever
        // the user has open now. Callers close the modal from onSuccess.
        onSuccess({ action: 'deny', affordableWaitlistId });
      },
      onError: (e) => toastr.error('Error', e?.message ?? e),
    },
  });

  // A dismissed modal keeps its mutation in flight, so a caller can be handed a
  // success for an action the user already replaced. Block dismissal while the
  // request is pending; callers also verify the payload's action.
  const handleDismiss = () => {
    if (isSubmitting) return;
    handleClose();
  };

  const ReactHookFormProps = useReactHookFormProps({
    defaultValues: { reasons: [], note: '' },
    mode: 'onChange',
  });
  const { getValues, isValid, isDirty } = ReactHookFormProps;

  const canAct = useHasPermission(WAITLIST_ACTION_SCOPE);

  const actionsProps = [
    {
      children: <FormattedMessage {...messages.dismiss} />,
      onClick: handleDismiss,
      disabled: isSubmitting,
    },
    {
      children: <FormattedMessage {...messages.confirm} />,
      submitButton: true,
      isSubmitting,
      disabled: !isValid || !isDirty,
      onClick: () => {
        const values = getValues();
        denyMutate({
          payload: {
            reasons: values.reasons,
            description: values.note ?? '',
          },
          affordableWaitlistId,
        });
      },
    },
  ];

  const modalProps = {
    title: <FormattedMessage {...messages.denyTitle} />,
    ContentProps: { sx: { width: { xs: 'calc(100% - 32px)', sm: 680 } } },
    actionsProps,
    // The endpoint refuses this without the scope, so the footer says so
    // rather than letting the agent submit into a 403.
    ...(canAct
      ? {}
      : {
          actions: (
            <PermissionGatedActions
              actionsProps={actionsProps}
              tooltip={<FormattedMessage {...messages.permissionRequired} />}
            />
          ),
        }),
  };

  return (
    <AffordableWaitlistActionModal
      open={open}
      handleClose={handleDismiss}
      modalProps={modalProps}
      ReactHookFormProps={ReactHookFormProps}
      reasons={denyReasons}
    />
  );
};

export default DenyFromWaitlist;
